import { LinearProgress, Stack, Typography } from "@mui/material";
import type { FC } from "react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import AsyncLoadingButton from "../components/asyncLoadingButton/AsyncLoadingButton";
import FormTextField, { emailValidationPattern } from "../components/formComponents/FormTextField";
import { auth } from "../auth/auth";
import przezmorzeLogo from "../../../images/przezmorze_192x192.png";

function getEmailAddress() {
  const email = localStorage.getItem("emailForSignIn");
  if (email) {
    return email;
  }
  return window.prompt("Podaj swój adres email");
}

interface EmailLoginFormState {
  email: string;
}

interface Props {
  readonly isSignInWithEmailLink: boolean;
  readonly onStartLogin: (email: string) => Promise<void>;
  readonly onLoginSuccess: () => void;
  readonly onLoginFailed: (error: unknown) => void;
}

const EmailLinkLogin: FC<Props> = ({ onStartLogin, isSignInWithEmailLink, onLoginSuccess, onLoginFailed }) => {
  const { control, handleSubmit } = useForm<EmailLoginFormState>({
    defaultValues: { email: "" },
  });
  const [showLinkSent, setShowLinkSent] = useState(false);

  useEffect(() => {
    if (isSignInWithEmailLink) {
      const email = getEmailAddress();
      if (!email) {
        onLoginFailed(new Error("Brak adresu email"));
        return;
      }
      auth
        .signInWithEmailLink(email, window.location.href)
        .then(() => {
          localStorage.removeItem("emailForSignIn");
          onLoginSuccess();
        })
        .catch((error) => {
          onLoginFailed(error);
        });
    }
  }, [isSignInWithEmailLink]);

  if (isSignInWithEmailLink) {
    return (
      <>
        <Typography>Sprawdzanie danych do logowania...</Typography>
        <LinearProgress />
      </>
    );
  }

  const handleStartLogin = async (email: string) => {
    await onStartLogin(email);
    localStorage.setItem("emailForSignIn", email);
    setShowLinkSent(true);
  };

  if (showLinkSent) {
    return (
      <Stack
        direction="column"
        spacing={2}
        sx={{
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        <img src={przezmorzeLogo} width={192} alt="Logo" />
        <Typography>Link pozwalający zalogować się do aplikacji został wysłany na wskazany adres email.</Typography>
        <Typography>Sprawdź swoją skrzynkę odbiorczą.</Typography>
      </Stack>
    );
  }

  return (
    <>
      <FormTextField
        type="email"
        name="email"
        label="Email"
        control={control}
        rules={{ pattern: emailValidationPattern }}
      />
      <AsyncLoadingButton onClick={handleSubmit((s) => handleStartLogin(s.email))}>Zaloguj</AsyncLoadingButton>
    </>
  );
};

export default EmailLinkLogin;
